import { ReactNode } from "react";
import { AlertTriangle, Clock, TrendingUp } from "lucide-react";

import { Sparkline } from "../../components/Sparkline/Sparkline";
import styles from "./StatCard.module.css";

/** Delta line content. tone "up" → green + trend arrow; "flat" → muted + clock. */
export interface StatDelta {
  tone: "up" | "flat" | "none";
  /** Optional bold leading value, e.g. "+214". */
  value?: string;
  /** Muted context text, e.g. "new · last 7 days". */
  context: string;
}

export interface StatCardProps {
  label: string;
  icon: ReactNode;
  /** Pre-formatted headline number, e.g. "12,847". */
  value: string;
  delta: StatDelta;
  /** 7 daily values for the sparkline, oldest → newest. */
  series: number[];
}

/** StatCard — one game metric. Used for both populated and empty (all-zero) data. */
export function StatCard({ label, icon, value, delta, series }: StatCardProps) {
  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <span className={styles.label}>{label}</span>
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      </div>
      <div className={styles.value}>{value}</div>
      <div
        className={`${styles.delta} ${
          delta.tone === "up" ? styles.up : styles.flat
        }`}
      >
        {delta.tone === "up" ? <TrendingUp size={12} aria-hidden="true" /> : null}
        {delta.tone === "flat" ? <Clock size={12} aria-hidden="true" /> : null}
        {delta.value ? <span>{delta.value}</span> : null}
        <span className={styles.ctx}>{delta.context}</span>
      </div>
      <div className={styles.sparkWrap}>
        <Sparkline data={series} />
      </div>
    </div>
  );
}

const SK_WIDTHS: { label: number; value: number; delta: number }[] = [
  { label: 62, value: 104, delta: 128 },
  { label: 54, value: 72, delta: 120 },
  { label: 96, value: 88, delta: 132 },
];

/** Skeleton placeholder shown while analytics load — holds the card's height. */
export function StatCardSkeleton({ variant = 0 }: { variant?: 0 | 1 | 2 }) {
  const w = SK_WIDTHS[variant];
  return (
    <div className={styles.card} aria-hidden="true">
      <div className={styles.head}>
        <span className={`${styles.sk} ${styles.skLabel}`} style={{ width: w.label }} />
        <span className={`${styles.sk} ${styles.skIcon}`} />
      </div>
      <span className={`${styles.sk} ${styles.skValue}`} style={{ width: w.value }} />
      <span className={`${styles.sk} ${styles.skDelta}`} style={{ width: w.delta }} />
      <span className={`${styles.sk} ${styles.skSpark}`} />
    </div>
  );
}

/**
 * The card while the project is being wiped: an em-dash, and a reason under
 * the first one.
 *
 * Distinct from StatCardError even though both draw a dash — nothing failed
 * here, the counting was deliberately stopped, and an operator who reads
 * "Couldn't load" during a wipe goes looking for a second problem. The mockup
 * (docs/mockups/project-wipe.html, `.stat.dim`) puts the sentence on the first
 * card only and leaves the others blank: it is one fact about the row, not
 * three about the cards.
 */
export function StatCardDim({
  label,
  icon,
  note,
}: {
  label: string;
  icon: ReactNode;
  note?: string;
}) {
  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <span className={styles.label}>{label}</span>
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      </div>
      <div className={`${styles.value} ${styles.valueDim}`}>—</div>
      {/* A non-breaking space where there is nothing to say: the line still
          has to hold its height, or the three cards stop lining up. */}
      <div className={`${styles.delta} ${styles.flat}`}>{note ?? "\u00A0"}</div>
      <svg
        className={styles.errSpark}
        viewBox="0 0 128 40"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <line x1="0" y1="38" x2="128" y2="38" />
      </svg>
    </div>
  );
}

/** Error variant — degrades to an em-dash; the rest of the page keeps working. */
export function StatCardError({
  label,
  icon,
}: {
  label: string;
  icon: ReactNode;
}) {
  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <span className={styles.label}>{label}</span>
        <span
          className={`${styles.icon} ${styles.iconError}`}
          aria-hidden="true"
        >
          {icon}
        </span>
      </div>
      <div className={`${styles.value} ${styles.valueDim}`}>—</div>
      <div className={`${styles.delta} ${styles.error}`}>
        <AlertTriangle size={12} aria-hidden="true" />
        Couldn&apos;t load
      </div>
      <svg
        className={styles.errSpark}
        viewBox="0 0 128 40"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <line x1="0" y1="38" x2="128" y2="38" />
      </svg>
    </div>
  );
}
